import { useState, useEffect, useRef } from 'react';
import $ from 'jquery';
import 'bootstrap-datepicker';
import 'bootstrap-datepicker/dist/css/bootstrap-datepicker.standalone.css';
import { useApp } from '../AppContext';
import { authApi, superAdminApi } from '../services/api';
import cFirstLogo from '../images/SwiftCheqLogo.png';
import Chip from '../components/Chip';

const PAGE_SIZE = 10;

// ── Helpers (same field mapping as the Ledger/Invoices/Reports pages) ──
function txnMeta(transType) {
  const t = (transType || '').toLowerCase();
  if (t.includes('top') || t === 'credit')              return { sign: '+', color: 'var(--gr)', chip: 'cg' };
  if (t.includes('refund'))                              return { sign: '+', color: 'var(--bl)', chip: 'cb' };
  if (t.includes('debit') || t.includes('order'))        return { sign: '-', color: 'var(--rd)', chip: 'cr' };
  if (t.includes('additional') || t.includes('charge'))  return { sign: '-', color: 'var(--am)', chip: 'ca' };
  return { sign: '', color: 'var(--nv)', chip: '' };
}
function fmt(n) { return Number(n || 0).toLocaleString('en-IN'); }
function encodeFile(name) {
  try { return btoa(unescape(encodeURIComponent(name))); } catch { return encodeURIComponent(name); }
}
function statusChipClass(status) {
  const s = (status || '').toLowerCase();
  if (s.includes('complet')) return 'cg';
  if (s.includes('progress')) return 'cb';
  if (s.includes('approval')) return 'cr';
  if (s.includes('need')) return 'ca';
  return '';
}
function initials(name) {
  return (name || '').split(' ').filter(Boolean).map(w => w[0]).join('').slice(0, 2).toUpperCase() || 'C';
}
const MON = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
function isoToDmy(iso) {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  return `${d}-${MON[parseInt(m, 10) - 1]}-${y}`;
}
function defaultDates() {
  const now = new Date();
  const toVal = isoToDmy(now.toISOString().slice(0, 10));
  const from = new Date(now);
  from.setMonth(from.getMonth() - 1);
  return { fromVal: isoToDmy(from.toISOString().slice(0, 10)), toVal };
}

// ── Dmy date picker (same bootstrap-datepicker control as the Reports page) ──
function DmyDatePicker({ value, onChange, placeholder = 'dd-Mon-yyyy' }) {
  const ref = useRef(null);

  useEffect(() => {
    const $input = $(ref.current);
    $input.datepicker({
      format: 'dd-M-yyyy',
      autoclose: true,
      todayHighlight: true,
      orientation: 'bottom auto',
    });
    $input.on('changeDate', () => onChange($input.val()));
    return () => { $input.datepicker('destroy'); $input.off('changeDate'); };
  }, []);

  return (
    <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
      <input
        ref={ref}
        className="fsel"
        type="text"
        readOnly
        defaultValue={value}
        placeholder={placeholder}
        style={{ cursor: 'pointer', paddingRight: 24, width: 'calc(11ch + 36px)' }}
      />
      <span style={{ position: 'absolute', right: 8, cursor: 'pointer', color: 'var(--g400)', pointerEvents: 'none', display: 'flex', alignItems: 'center' }}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
        </svg>
      </span>
    </div>
  );
}

// ── Pager (mirrors the mockup's .pag / .pgbtn markup, wired to click) ──
// Windowed page list: 1 2 3 … N when near the start, 1 … n-1 n n+1 … N in the middle.
function pageWindow(current, total) {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const set = new Set([1, total, current]);
  if (current <= 3) { set.add(2); set.add(3); }
  else if (current >= total - 2) { set.add(total - 1); set.add(total - 2); }
  else { set.add(current - 1); set.add(current + 1); }
  const sorted = [...set].sort((a, b) => a - b);
  const items = [];
  sorted.forEach((p, i) => {
    if (i > 0 && p - sorted[i - 1] > 1) items.push(`e${p}`);
    items.push(p);
  });
  return items;
}

function Pager({ page, totalPages, onChange, force }) {
  if (totalPages <= 1 && !force) return null;
  return (
    <div className="pag">
      <button className="pgbtn" onClick={() => onChange(Math.max(1, page - 1))} disabled={page === 1}>&lsaquo;</button>
      {pageWindow(page, totalPages).map(item => (
        typeof item === 'number'
          ? <button key={item} className={`pgbtn ${item === page ? 'on' : ''}`} onClick={() => onChange(item)}>{item}</button>
          : <span key={item} className="pgbtn" style={{ border: 'none', cursor: 'default' }}>…</span>
      ))}
      <button className="pgbtn" onClick={() => onChange(Math.min(totalPages, page + 1))} disabled={page === totalPages}>&rsaquo;</button>
    </div>
  );
}

// ── Client picker (searchable dropdown, opens on click) ─────────
// Kept in code but hidden from the console now that navigation happens
// from the AdminViewClients table — see the `.cl-picker` block below.
function ClientPicker({ clients, loading, selectedId, onSelect }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handle = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [open]);

  useEffect(() => {
    if (open) { setSearch(''); inputRef.current?.focus(); }
  }, [open]);

  const selected = clients.find(c => String(c.clientid) === String(selectedId));
  const label = loading
    ? 'Loading clients…'
    : (selected ? `${selected.clientname}${selected.clientnumber ? ` (${selected.clientnumber})` : ''}` : 'Select a client…');

  const q = search.trim().toLowerCase();
  const filtered = q
    ? clients.filter(c => (c.clientname || '').toLowerCase().includes(q) || (c.clientnumber || '').toLowerCase().includes(q))
    : clients;

  function pick(c) { onSelect(c.clientid); setOpen(false); }

  return (
    <div ref={ref} style={{ position: 'relative', width: 260, flexShrink: 0 }}>
      <button
        type="button"
        className="fsel"
        onClick={() => setOpen(o => !o)}
        disabled={loading || clients.length === 0}
        style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', overflow: 'hidden' }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
        <span style={{ fontSize: 9, color: 'var(--g400)', marginLeft: 6, flexShrink: 0 }}>▼</span>
      </button>

      {open && (
        <div style={{
          position: 'absolute', right: 0, top: 'calc(100% + 6px)',
          background: 'var(--wh)', border: '1px solid var(--g200)',
          borderRadius: 10, boxShadow: '0 6px 32px rgba(0,0,0,.14)',
          width: 300, zIndex: 400, overflow: 'hidden',
        }}>
          <div style={{ padding: '8px 10px', borderBottom: '1px solid var(--g100)' }}>
            <input
              ref={inputRef}
              className="inp"
              placeholder="Search by name or code…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{ margin: 0 }}
            />
          </div>
          <div style={{ maxHeight: 280, overflowY: 'auto' }}>
            {filtered.length === 0 && (
              <div style={{ padding: 16, textAlign: 'center', fontSize: 12, color: 'var(--g400)' }}>No clients found</div>
            )}
            {filtered.map(c => {
              const isSelected = String(c.clientid) === String(selectedId);
              return (
                <div
                  key={c.clientid}
                  onClick={() => pick(c)}
                  onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = 'var(--g50)'; }}
                  onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = 'transparent'; }}
                  style={{
                    padding: '9px 14px', cursor: 'pointer', fontSize: 12.5,
                    background: isSelected ? 'var(--g50)' : 'transparent',
                    borderBottom: '1px solid var(--g50)',
                  }}
                >
                  <div style={{ fontWeight: 600, color: 'var(--nv)' }}>{c.clientname}</div>
                  {c.clientnumber && <div style={{ fontSize: 11, color: 'var(--g400)' }}>{c.clientnumber}</div>}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ── NavItem ────────────────────────────────────────────────────
function NavItem({ icon, label, onClick, danger }) {
  const [hov, setHov] = useState(false);
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        display: 'flex', alignItems: 'center', gap: 9, padding: '10px 16px',
        fontSize: 13, cursor: 'pointer',
        color: danger ? 'var(--rd)' : 'var(--nv)',
        fontWeight: danger ? 600 : 400,
        background: hov ? (danger ? 'rgba(220,38,38,.05)' : 'var(--g50)') : 'transparent',
        transition: 'background .12s',
      }}
    >
      <span style={{ fontSize: 14 }}>{icon}</span>
      {label}
    </div>
  );
}

// ── SuperAdmin Nav ─────────────────────────────────────────────
export function SuperAdminNav({ onNavigate, onChangePassword, currentPage }) {
  const { user, logout } = useApp();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handle = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [open]);

  async function doLogout() {
    setOpen(false);
    await logout();
    onNavigate('login');
  }

  const name = user?.UserName || user?.username || 'Super Admin';
  const nameInitials = name.split(' ').filter(Boolean).map(w => w[0]).join('').slice(0, 2).toUpperCase() || 'SA';

  return (
    <nav className="topnav">
      <div className="logo">
        <img src={cFirstLogo} alt="cFIRST BGV" style={{ height: 36, width: 'auto' }} />
      </div>
      <div className="navmenu">
        <div
          className={`ni ${currentPage === 'admin-dashboard' ? 'on' : ''}`}
          onClick={() => onNavigate('admin-dashboard')}
        >
          Dashboard
        </div>
        <div
          className={`ni ${currentPage === 'admin-view-clients' ? 'on' : ''}`}
          onClick={() => onNavigate('admin-view-clients')}
        >
          All Clients
        </div>
        <div
          className={`ni ${currentPage === 'admin-client-registrations' ? 'on' : ''}`}
          onClick={() => onNavigate('admin-client-registrations')}
        >
          Registration List
        </div>
        <div
          className={`ni ${currentPage === 'admin-payment-transactions' ? 'on' : ''}`}
          onClick={() => onNavigate('admin-payment-transactions')}
        >
          Payment Transactions
        </div>
      </div>
      <div ref={ref} style={{ position: 'relative' }}>
        <div
          onClick={() => setOpen(o => !o)}
          style={{
            display: 'flex', alignItems: 'center', gap: 8, padding: '6px 12px',
            borderRadius: 8, background: 'var(--g50)', border: '1px solid var(--g200)',
            cursor: 'pointer', userSelect: 'none',
          }}
        >
          <div className="av" style={{ pointerEvents: 'none' }}>{nameInitials}</div>
          <div>
            <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--nv)', lineHeight: 1.3 }}>{name}</div>
            <div style={{ fontSize: 10, color: 'var(--or)', fontWeight: 600, lineHeight: 1.3 }}>Super Admin</div>
          </div>
          <span style={{ fontSize: 9, color: 'var(--g400)', marginLeft: 2 }}>▼</span>
        </div>

        {open && (
          <div style={{
            position: 'absolute', right: 0, top: 'calc(100% + 8px)',
            background: 'var(--wh)', border: '1px solid var(--g200)',
            borderRadius: 10, boxShadow: '0 4px 24px rgba(0,0,0,.13)',
            minWidth: 200, zIndex: 300, overflow: 'hidden',
          }}>
            {/* <NavItem icon="🔑" label="Change Password" onClick={() => { onChangePassword(); setOpen(false); }} /> */}
            <div style={{ borderTop: '1px solid var(--g100)' }}>
              <NavItem icon="🚪" label="Logout" onClick={doLogout} danger />
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}

// ── Change Password Modal ──────────────────────────────────────
function ChangePasswordModal({ onClose, addToast }) {
  const [oldPass, setOldPass] = useState('');
  const [newPass, setNewPass] = useState('');
  const [saving, setSaving] = useState(false);

  async function handleSave() {
    if (!oldPass.trim() || !newPass.trim()) { addToast('Please fill all fields', 'error'); return; }
    setSaving(true);
    try {
      const res = await authApi.changePassword(oldPass, newPass);
      addToast(res?.message || 'Password changed successfully', 'success');
      onClose();
    } catch (err) {
      addToast(err.message || 'Failed to change password', 'error');
    } finally { setSaving(false); }
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', zIndex: 999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div className="card" style={{ width: 380, padding: 28, boxShadow: '0 8px 40px rgba(0,0,0,.2)' }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--nv)', marginBottom: 20 }}>🔑 Change Password</div>
        <div className="fg" style={{ marginBottom: 14 }}>
          <label className="flbl">Current Password</label>
          <input className="inp" type="password" value={oldPass} onChange={e => setOldPass(e.target.value)} />
        </div>
        <div className="fg" style={{ marginBottom: 20 }}>
          <label className="flbl">New Password</label>
          <input className="inp" type="password" value={newPass} onChange={e => setNewPass(e.target.value)} onKeyDown={e => { if (e.key === 'Enter' && !saving) handleSave(); }} />
        </div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button className="btn bout" onClick={onClose}>Cancel</button>
          <button className="btn bpri" onClick={handleSave} disabled={saving}>{saving ? <><span className="spinner" style={{ width: 14, height: 14 }} /> Saving…</> : 'Save Changes'}</button>
        </div>
      </div>
    </div>
  );
}

// ── Ledger tab (data: superAdminApi, same field mapping as the Ledger page) ─
function LedgerPanel({ clientId, clientName }) {
  const [txns, setTxns] = useState([]);
  const [walletBal, setWalletBal] = useState(null);
  const [loading, setLoading] = useState(false);
  const [typeFilter, setTypeFilter] = useState('');
  const [page, setPage] = useState(1);

  useEffect(() => {
    if (!clientId) return;
    setLoading(true);
    setTxns([]);
    setTypeFilter('');
    setPage(1);
    setWalletBal(null);
    Promise.all([
      superAdminApi.getWalletTransactions(clientId).catch(() => []),
      superAdminApi.getWalletDetails(clientId).catch(() => null),
    ]).then(([txnData, walletData]) => {
      setTxns(Array.isArray(txnData) ? txnData : (txnData?.transactions || []));
      const record = Array.isArray(walletData) ? walletData[0] : walletData;
      if (record) setWalletBal(parseFloat(record.current_balance) || 0);
    }).finally(() => setLoading(false));
  }, [clientId]);

  const uniqueTypes = [...new Set(txns.map(t => t.trans_type).filter(Boolean))];
  const filtered = typeFilter ? txns.filter(t => t.trans_type === typeFilter) : txns;
  const totalAdded    = txns.filter(t => txnMeta(t.trans_type).sign === '+' && !(t.trans_type || '').toLowerCase().includes('refund')).reduce((s, t) => s + Number(t.amount || 0), 0);
  const totalSpent    = txns.filter(t => txnMeta(t.trans_type).sign === '-').reduce((s, t) => s + Number(t.amount || 0), 0);
  const totalRefunded = txns.filter(t => (t.trans_type || '').toLowerCase().includes('refund')).reduce((s, t) => s + Number(t.amount || 0), 0);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const curPage = Math.min(page, totalPages);
  const pageItems = filtered.slice((curPage - 1) * PAGE_SIZE, curPage * PAGE_SIZE);
  const showFrom = filtered.length === 0 ? 0 : (curPage - 1) * PAGE_SIZE + 1;
  const showTo = Math.min(curPage * PAGE_SIZE, filtered.length);

  return (
    <>
      <div className="phd" style={{ marginBottom: 16 }}>
        <div>
          <div className="ctitle" style={{ fontSize: 15 }}>Wallet &amp; Credit Ledger</div>
          <div className="psub">Full transaction history for {clientName || 'the selected client'}'s prepaid account</div>
        </div>
      </div>

      <div className="gr4 mb20">
        <div className="sc"><div className="si si-or">💰</div><div><div className="slbl">Current Balance</div><div className="sval">₹{fmt(walletBal)}</div></div></div>
        <div className="sc"><div className="si si-gr">➕</div><div><div className="slbl">Total Added</div><div className="sval">₹{fmt(totalAdded)}</div></div></div>
        <div className="sc"><div className="si si-rd">➖</div><div><div className="slbl">Total Spent</div><div className="sval">₹{fmt(totalSpent)}</div></div></div>
        <div className="sc"><div className="si si-bl">🔄</div><div><div className="slbl">Refunded</div><div className="sval">₹{fmt(totalRefunded)}</div></div></div>
      </div>

      <div className="card">
        <div className="chd">
          <div><div className="ctitle">Transaction History</div><div className="csub">{txns.length} total transaction{txns.length !== 1 ? 's' : ''}</div></div>
          <div className="flex g8">
            <select className="fsel" value={typeFilter} onChange={e => { setTypeFilter(e.target.value); setPage(1); }}>
              <option value="">All Types</option>
              {uniqueTypes.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
        </div>
        <div className="twrap">
          <table>
            <thead>
              <tr><th>Date &amp; Time</th><th>Description</th><th>Type</th><th>Amount</th><th>Running Balance</th></tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={5} style={{ textAlign: 'center', padding: 24, color: 'var(--g400)' }}>Loading…</td></tr>}
              {!loading && pageItems.length === 0 && (
                <tr><td colSpan={5} style={{ textAlign: 'center', padding: 24, color: 'var(--g400)' }}>No transactions found</td></tr>
              )}
              {!loading && pageItems.map((t, i) => {
                const m = txnMeta(t.trans_type);
                return (
                  <tr key={t.trans_id ?? i}>
                    <td className="tdsm">{t.created_on || '—'}</td>
                    <td>
                      <div className="td1">{t.remarks || '—'}</div>
                      {t.reference_type && <div className="tdm">{t.reference_type} {t.reference_id || ''}</div>}
                    </td>
                    <td><Chip label={t.trans_type || '—'} className={m.chip} /></td>
                    <td style={{ fontWeight: 700, color: m.color }}>{m.sign}₹{fmt(t.amount)}</td>
                    <td style={{ fontWeight: 700 }}>₹{fmt(t.balance_after)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="flex jb aic mt16">
          <div className="ts tg">Showing {showFrom}–{showTo} of {filtered.length} transaction{filtered.length !== 1 ? 's' : ''}</div>
          <Pager page={curPage} totalPages={totalPages} onChange={setPage} />
        </div>
      </div>
    </>
  );
}

// ── Invoice tab (data: superAdminApi, same field mapping as the Invoices page) ─
function InvoicePanel({ clientId, clientName, gstin, email }) {
  const { addToast } = useApp();
  const [invs, setInvs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [downloading, setDownloading] = useState(null);

  useEffect(() => {
    if (!clientId) return;
    setLoading(true);
    setInvs([]);
    setPage(1);
    superAdminApi.getInvoiceHistory(clientId)
      .then(data => setInvs(Array.isArray(data) ? data : (data?.invoices || [])))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [clientId]);

  async function handleDownload(inv) {
    const key = inv.invoicenumber;
    if (downloading === key) return;
    setDownloading(key);
    try {
      const url = `${process.env.REACT_APP_API_URL}/wallet/DownloadInvoice?file=${encodeFile(inv.invoicenumber)}`;
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token') || ''}` },
        credentials: 'include',
      });
      if (!res.ok) throw new Error(`Server error ${res.status}`);
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = `${inv.invoicenumber || 'invoice'}.pdf`;
      document.body.appendChild(a); a.click();
      document.body.removeChild(a); URL.revokeObjectURL(objectUrl);
    } catch (e) {
      addToast(`Download failed: ${e.message}`, 'error');
    } finally { setDownloading(null); }
  }

  const totalCredits = invs.reduce((s, i) => s + Number(i.totalamount   || 0), 0);
  const totalGst     = invs.reduce((s, i) => s + Number(i.gstamount     || 0), 0);
  const totalPaid    = invs.reduce((s, i) => s + Number(i.payableamount || 0), 0);
  const totalPages   = Math.max(1, Math.ceil(invs.length / PAGE_SIZE));
  const curPage      = Math.min(page, totalPages);
  const pageInvs     = invs.slice((curPage - 1) * PAGE_SIZE, curPage * PAGE_SIZE);
  const showFrom     = invs.length === 0 ? 0 : (curPage - 1) * PAGE_SIZE + 1;
  const showTo       = Math.min(curPage * PAGE_SIZE, invs.length);

  return (
    <>
      <div className="phd" style={{ marginBottom: 16 }}>
        <div>
          <div className="ctitle" style={{ fontSize: 15 }}>GST Invoices</div>
          <div className="psub">All credit top-up invoices for {clientName || 'the selected client'} — for accounting &amp; GST input credit</div>
        </div>
      </div>

      <div className="gr4 mb20">
        <div className="sc"><div className="si si-bl">📄</div><div><div className="slbl">Total Invoices</div><div className="sval">{invs.length}</div></div></div>
        <div className="sc"><div className="si si-gr">💰</div><div><div className="slbl">Credits Purchased</div><div className="sval" style={{ fontSize: 19 }}>₹{fmt(totalCredits)}</div></div></div>
        <div className="sc"><div className="si si-or">🏛️</div><div><div className="slbl">GST Paid (18%)</div><div className="sval" style={{ fontSize: 19 }}>₹{fmt(totalGst)}</div></div></div>
        <div className="sc"><div className="si si-pu">💳</div><div><div className="slbl">Total Paid (incl. GST)</div><div className="sval" style={{ fontSize: 19 }}>₹{fmt(totalPaid)}</div></div></div>
      </div>

      <div className="card">
        <div className="chd">
          <div><div className="ctitle">Invoice History</div><div className="csub">GST invoices raised by cFIRST Background Chekcs LLP.</div></div>
        </div>
        <div className="twrap">
          <table>
            <thead>
              <tr><th>Invoice No.</th><th>Date</th><th>Payment Ref.</th><th>Credits</th><th>GST (18%)</th><th>Total Paid</th><th>Status</th><th>Method</th><th>Download</th></tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={9} style={{ textAlign: 'center', padding: 24, color: 'var(--g400)' }}>Loading…</td></tr>}
              {!loading && pageInvs.length === 0 && (
                <tr><td colSpan={9} style={{ textAlign: 'center', padding: 24, color: 'var(--g400)' }}>No invoices found</td></tr>
              )}
              {!loading && pageInvs.map((inv, i) => {
                const hasPdf = !!(inv.invoicename || inv.invoicenumber);
                const isDownloading = downloading === inv.invoicenumber;
                const isGenerated = (inv.invoicestatus || '').toUpperCase() === 'GENERATED';
                return (
                  <tr key={inv.invoicenumber || i}>
                    <td className="tdmono">{inv.invoicenumber || '—'}</td>
                    <td className="tdsm">{inv.createdon || '—'}</td>
                    <td className="tdmono">{inv.transaction_id || '—'}</td>
                    <td className="tdsm" style={{ fontWeight: 700, color: 'var(--or-dk)' }}>₹{fmt(inv.totalamount)}</td>
                    <td className="tdsm">₹{fmt(inv.gstamount)}</td>
                    <td className="f7">₹{fmt(inv.payableamount)}</td>
                    <td><Chip label={inv.invoicestatus || '—'} className={isGenerated ? 'ca' : 'cg'} /></td>
                    <td className="tdsm">{inv.invoicetype || '—'}</td>
                    <td>
                      <button
                        className="btn bout bsm"
                        onClick={() => handleDownload(inv)}
                        disabled={!hasPdf || isDownloading}
                        style={!hasPdf ? { opacity: .5 } : undefined}
                      >
                        {isDownloading ? <span className="spinner" style={{ width: 11, height: 11 }} /> : '⬇ PDF'}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="flex jb aic mt16">
          <div className="ts tg">Showing {showFrom}–{showTo} of {invs.length} invoice{invs.length !== 1 ? 's' : ''}</div>
          <Pager page={curPage} totalPages={totalPages} onChange={setPage} />
        </div>
        <div className="alert a-bl mt16">
          <div className="aico">ℹ️</div>
          <div className="abody">GST invoices are issued by cFIRST Background Chekcs LLP. Client GSTIN on file: <strong>{gstin || '—'}</strong>. Invoices are emailed to {email || 'the registered accounts email'} within 24 hrs of payment confirmation.</div>
        </div>
      </div>
    </>
  );
}

// ── Reports tab (data: superAdminApi, same field mapping as the Reports page) ─
function ReportsPanel({ clientId, clientName }) {
  const { fromVal: defFrom, toVal: defTo } = defaultDates();
  const [fromDate, setFromDate] = useState(defFrom);
  const [toDate, setToDate] = useState(defTo);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);

  function changePageSize(v) { setPageSize(Number(v)); setPage(1); }

  function fetchData(from, to) {
    if (!clientId) return;
    setLoading(true);
    setPage(1);
    superAdminApi.getCandidateWiseBreakdown(from, to, clientId)
      .then(data => setRows(Array.isArray(data) ? data : (data?.data || [])))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }

  function exportToExcel() {
    const esc = v => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const headers = ['Sr.', 'Candidate Name', 'Emp. ID', 'Order Date', 'Completion', 'Base Credits', 'Addl. Charges', 'Total', 'Status'];
    const dataRows = rows.map((r, i) => [
      i + 1,
      esc(r.full_name),
      esc(r.employment_number),
      esc(r.creation_date),
      esc(r.report_completion_date),
      Number(r.base_amount || 0),
      Number(r.additional_charge || 0),
      Number(r.total || 0),
      esc(r.status),
    ].join(','));
    const csv = '﻿' + [headers.join(','), ...dataRows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Candidate_Breakdown_${fromDate}_to_${toDate}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  useEffect(() => {
    if (clientId) fetchData(defFrom, defTo);
  }, [clientId]); // eslint-disable-line

  const totalBase = rows.reduce((s, r) => s + Number(r.base_amount       || 0), 0);
  const totalAddl = rows.reduce((s, r) => s + Number(r.additional_charge || 0), 0);
  const totalAll  = rows.reduce((s, r) => s + Number(r.total             || 0), 0);
  const countCompleted  = rows.filter(r => (r.status || '').toLowerCase().includes('complet')).length;
  const countInProgress = rows.filter(r => (r.status || '').toLowerCase().includes('progress')).length;
  const countNeedInfo   = rows.filter(r => (r.status || '').toLowerCase().includes('need')).length;
  const countApproval   = rows.filter(r => (r.status || '').toLowerCase().includes('approval')).length;

  const totalPages = Math.max(1, Math.ceil(rows.length / pageSize));

  // Keep page in bounds whenever data or pageSize changes
  useEffect(() => { // eslint-disable-line
    const max = Math.max(1, Math.ceil(rows.length / pageSize));
    if (page > max) setPage(max);
  }, [rows.length, pageSize]);

  const curPage = Math.min(page, totalPages);
  const pageRows = rows.slice((curPage - 1) * pageSize, curPage * pageSize);
  const showFrom = rows.length === 0 ? 0 : (curPage - 1) * pageSize + 1;
  const showTo = Math.min(curPage * pageSize, rows.length);
  const periodLabel = fromDate && toDate ? `${fromDate} to ${toDate}` : '—';

  return (
    <>
      <div className="phd" style={{ marginBottom: 16 }}>
        <div>
          <div className="ctitle" style={{ fontSize: 15 }}>Monthly Usage &amp; Billing Annexure</div>
          <div className="psub">Detailed breakdown of all BGV orders for {clientName || 'the selected client'}</div>
        </div>
        <div className="flex g8 aic" style={{ flexWrap: 'wrap' }}>
          <div className="flex g8 aic">
            <label className="flbl" style={{ margin: 0, whiteSpace: 'nowrap' }}>From</label>
            <DmyDatePicker value={fromDate} onChange={setFromDate} />
            <label className="flbl" style={{ margin: 0, whiteSpace: 'nowrap' }}>To</label>
            <DmyDatePicker value={toDate} onChange={setToDate} />
            <button className="btn bpri" onClick={() => fetchData(fromDate, toDate)} disabled={loading}>{loading ? <span className="spinner" style={{ width: 12, height: 12 }} /> : '🔍 Search'}</button>
          </div>
          <button className="btn bout" onClick={exportToExcel} disabled={rows.length === 0}>⬇ Export Excel</button>
        </div>
      </div>

      <div className="ubanner">
        <div className="ubi"><div className="ubl">Period</div><div className="ubv" style={{ fontSize: 13 }}>{periodLabel}</div></div>
        <div className="ubi"><div className="ubl">Total Candidates</div><div className="ubv or">{rows.length}</div></div>
        <div className="ubi"><div className="ubl">Base Credits</div><div className="ubv or">₹{fmt(totalBase)}</div></div>
        <div className="ubi"><div className="ubl">Addl. Charges</div><div className="ubv">₹{fmt(totalAddl)}</div></div>
      </div>

      <div className="gr4 mb20">
        <div className="sc"><div className="si si-gr">✅</div><div><div className="slbl">Completed</div><div className="sval">{countCompleted}</div></div></div>
        <div className="sc"><div className="si si-bl">🔄</div><div><div className="slbl">In Progress</div><div className="sval">{countInProgress}</div></div></div>
        <div className="sc"><div className="si si-am">⚠️</div><div><div className="slbl">Need Info</div><div className="sval">{countNeedInfo}</div></div></div>
        <div className="sc"><div className="si si-rd">🔴</div><div><div className="slbl">Approval Needed</div><div className="sval">{countApproval}</div></div></div>
      </div>

      <div className="card">
        <div className="chd"><div><div className="ctitle">Candidate-wise Breakdown — {periodLabel}</div><div className="csub">{clientName}</div></div></div>
        <div className="twrap">
          <table>
            <thead>
              <tr>
                <th>Sr.</th><th>Ref No.</th><th>Candidate</th><th>Emp. ID</th><th>Order Date</th>
                <th>Completion</th><th>Base Credits</th><th>Addl. Charges</th><th>Total</th><th>Status</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={10} style={{ textAlign: 'center', padding: 24, color: 'var(--g400)' }}>Loading…</td></tr>}
              {!loading && pageRows.length === 0 && (
                <tr><td colSpan={10} style={{ textAlign: 'center', padding: 24, color: 'var(--g400)' }}>No records found for this period</td></tr>
              )}
              {!loading && pageRows.map((r, i) => {
                const globalIdx = (curPage - 1) * pageSize + i;
                return (
                  <tr key={globalIdx}>
                    <td className="tdsm">{globalIdx + 1}</td>
                    <td className="tdsm">{r.profile_number || '—'}</td>
                    <td className="td1">{r.full_name || '—'}</td>
                    <td className="tdmono">{r.employment_number || '—'}</td>
                    {/* No per-candidate check/package type is returned by CandidateWiseBreakdown */}
                    <td className="tdsm">{r.creation_date || '—'}</td>
                    <td className="tdsm">{r.report_completion_date || '—'}</td>
                    <td className="tdsm">₹{fmt(r.base_amount)}</td>
                    <td className="tdsm" style={{ color: Number(r.additional_charge) > 0 ? 'var(--rd)' : undefined }}>₹{fmt(r.additional_charge)}</td>
                    <td className="f7">₹{fmt(r.total)}</td>
                    <td><Chip label={r.status || '—'} className={statusChipClass(r.status)} /></td>
                  </tr>
                );
              })}
            </tbody>
            {rows.length > 0 && (
              <tfoot>
                <tr>
                  <td colSpan={6} style={{ padding: '10px 12px', fontSize: 12.5, fontWeight: 700, color: 'var(--or-dk)', background: 'var(--or-lt)' }}>
                    TOTAL — {periodLabel} ({rows.length} candidate{rows.length !== 1 ? 's' : ''})
                  </td>
                  <td style={{ padding: '10px 12px', fontWeight: 800, color: 'var(--or-dk)', background: 'var(--or-lt)' }}>₹{fmt(totalBase)}</td>
                  <td style={{ padding: '10px 12px', fontWeight: 800, color: 'var(--rd)', background: 'var(--or-lt)' }}>₹{fmt(totalAddl)}</td>
                  <td style={{ padding: '10px 12px', fontWeight: 800, color: 'var(--or-dk)', background: 'var(--or-lt)', fontSize: 14 }}>₹{fmt(totalAll)}</td>
                  <td style={{ background: 'var(--or-lt)' }}></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
        <div className="flex jb aic mt16">
          <div className="flex g8 aic">
            <span className="ts tg" style={{ whiteSpace: 'nowrap' }}>Rows per page:</span>
            <select className="fsel" style={{ width: 64 }} value={pageSize} onChange={e => changePageSize(e.target.value)}>
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
            <span className="ts tg" style={{ whiteSpace: 'nowrap', paddingLeft: 8 }}>Showing {showFrom}–{showTo} of {rows.length}</span>
          </div>
          <Pager page={curPage} totalPages={totalPages} onChange={setPage} force />
        </div>
      </div>
    </>
  );
}

// ── Main SuperAdminConsole ─────────────────────────────────────
export default function SuperAdminConsole({ onNavigate, clientId: initialClientId, clientName: initialClientName }) {
  const { addToast } = useApp();
  const [tab, setTab] = useState('ledger');
  const [clients, setClients] = useState([]);
  const [loadingClients, setLoadingClients] = useState(true);
  const [selectedClient, setSelectedClient] = useState(
    initialClientId ? { clientid: initialClientId, clientname: initialClientName || '' } : null
  );
  const [walletDetails, setWalletDetails] = useState(null);
  const [changePwModal, setChangePwModal] = useState(false);

  // Dropdown is hidden (navigation now happens from the AdminViewClients table),
  // but we still fetch this list so a client can be preselected/looked up by id.
  useEffect(() => {
    setLoadingClients(true);
    superAdminApi.getClients()
      .then(data => {
        const list = Array.isArray(data) ? data : (data?.data || []);
        setClients(list);
        const preselected = initialClientId && list.find(c => String(c.clientid) === String(initialClientId));
        if (preselected) setSelectedClient(preselected);
        else if (list.length && !initialClientId) setSelectedClient(list[0]);
      })
      .catch(() => {})
      .finally(() => setLoadingClients(false));
    // eslint-disable-next-line
  }, []);

  const clientId       = selectedClient?.clientid ?? null;
  const clientNumber   = selectedClient?.clientnumber ?? '';
  const clientNameOpt  = selectedClient?.clientname ?? '';

  // Same source as the client's own Wallet page — used to fill in the client-info strip
  useEffect(() => {
    if (!clientId) { setWalletDetails(null); return; }
    superAdminApi.getWalletDetails(clientId)
      .then(data => setWalletDetails(Array.isArray(data) ? data[0] : data))
      .catch(() => setWalletDetails(null));
  }, [clientId]);

  function handleSelectClient(id) {
    const match = clients.find(c => String(c.clientid) === String(id));
    setSelectedClient(match || null);
    setTab('ledger');
  }

  const displayName = walletDetails?.client_name || clientNameOpt;
  const gstin = walletDetails?.gstinofbill || '';
  const email = walletDetails?.invoice_copy_email || '';
  const metaParts = [clientNumber, gstin].filter(Boolean);

  return (
    <>
      <SuperAdminNav onNavigate={onNavigate} onChangePassword={() => setChangePwModal(true)} />
      {changePwModal && <ChangePasswordModal onClose={() => setChangePwModal(false)} addToast={addToast} />}

      <div className="pg">
        <div className="phd">
          <div>
            <div className="ptitle">Client Ledger, Invoices &amp; Reports</div>
            <div className="psub">Select a client to view their prepaid wallet ledger, GST invoices and monthly usage reports.</div>
          </div>
          <button
            className="btn bghost"
            onClick={() => onNavigate('admin-view-clients')}
          >
            <span style={{ fontSize: 14 }}>←</span> Back to Clients
          </button>
        </div>

        {/* CLIENT SELECTOR */}
        <div className="clientbar">
          <div className="cl-id">
            <div className="cl-avatar" style={{ background: 'var(--nv)' }}>{initials(displayName)}</div>
            <div>
              <div className="cl-name">{displayName || (loadingClients ? 'Loading…' : 'No client selected')}</div>
              <div className="cl-meta">{metaParts.join(' · ')}</div>
            </div>
          </div>
          <div className="cl-facts">
            {email && <div className="cl-fact">Accounts email <strong>{email}</strong></div>}
            {walletDetails && <div className="cl-fact">Wallet balance <strong>₹{fmt(walletDetails.current_balance)}</strong></div>}
          </div>
          {/* Hidden: navigation now happens from the AdminViewClients table. Kept in code for potential re-enable. */}
          <div className="cl-picker" style={{ display: 'none' }}>
            <label>Client</label>
            <ClientPicker clients={clients} loading={loadingClients} selectedId={clientId} onSelect={handleSelectClient} />
          </div>
        </div>

        {/* TABS */}
        <div className="tabbar mb16">
          <div className={`tab ${tab === 'ledger' ? 'on' : ''}`} onClick={() => setTab('ledger')}>📒 Ledger</div>
          <div className={`tab ${tab === 'invoice' ? 'on' : ''}`} onClick={() => setTab('invoice')}>🧾 Invoice</div>
          <div className={`tab ${tab === 'reports' ? 'on' : ''}`} onClick={() => setTab('reports')}>📊 Reports</div>
        </div>

        {!clientId ? (
          <div style={{ padding: '48px 0', textAlign: 'center', color: 'var(--g400)', fontSize: 13 }}>
            {loadingClients ? 'Loading clients…' : 'Select a client above to view their console'}
          </div>
        ) : (
          <>
            {tab === 'ledger'  && <LedgerPanel  clientId={clientId} clientName={displayName} />}
            {tab === 'invoice' && <InvoicePanel clientId={clientId} clientName={displayName} gstin={gstin} email={email} />}
            {tab === 'reports' && <ReportsPanel clientId={clientId} clientName={displayName} />}
          </>
        )}
      </div>
    </>
  );
}
